import { ReactNode } from 'react';
import { RouterLink } from 'design-system/src/components/Button/Button';
import { formatSimpleDate, formatStandardDate } from 'shared-utils/utils/formatDate';
import { PrescriptionLinkList, type PrescriptionLink } from '../PrescriptionLinkList/PrescriptionLinkList';

export type BuildGeneralInfoTableDataParams = {
  patientName: string | null;
  prescriberName: string | null;
  dob: string | null;
  prescribedOn: string | null;
  drugName: string | null;
  prescriptionId: string | null;
  linkDrugToRxPage?: boolean; // Whether to show the drug name as a link to the Rx page
  relatedPrescriptions?: PrescriptionLink[];
  extraData?: Array<{ label: string; value: string | null | ReactNode }>;
};

/**
 * Builds the data array for GeneralInfoTable components.
 * Handles formatting patient/prescriber names with dates and drug information.
 */
export const buildGeneralInfoTableData = ({
  patientName,
  prescriberName,
  dob,
  prescribedOn,
  drugName,
  prescriptionId,
  linkDrugToRxPage,
  relatedPrescriptions = [],
  extraData = [],
}: BuildGeneralInfoTableDataParams): Array<{ label: string; value: string | null | ReactNode }> => {
  let patientValue = patientName;
  if (dob && patientValue !== null) {
    patientValue += ` (${formatStandardDate(dob)})`;
  }
  let prescriberValue = prescriberName;
  if (prescribedOn && prescriberValue !== null) {
    prescriberValue += ` on ${formatSimpleDate(prescribedOn)}`;
  }

  const generalData = [];
  if (drugName && prescriptionId) {
    if (linkDrugToRxPage === false) {
      generalData.push({
        label: 'Drug',
        value: drugName,
      });
    } else {
      // Show either one or a list of prescriptions in the Drug section
      if (relatedPrescriptions.length > 0) {
        const prescriptions = [{ id: prescriptionId, name: drugName }, ...relatedPrescriptions];
        generalData.push({
          label: 'Drug',
          value: <PrescriptionLinkList prescriptions={prescriptions} />,
        });
      } else {
        generalData.push({
          label: 'Drug',
          value: <RouterLink to={`/rxs/${prescriptionId}`}>{drugName}</RouterLink>,
        });
      }
    }
  }
  generalData.push({ label: 'Patient', value: patientValue });
  generalData.push({ label: 'Prescribed by', value: prescriberValue });
  return [...generalData, ...extraData];
};
